import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../connector/api';

export default function ResetConfirm() {
    const { uid, token } = useParams();
    const navigate = useNavigate();
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    // Password Validation Rules State
    const [validations, setValidations] = useState({
        minLength: false,
        hasNumber: false,
        hasSpecial: false,
        hasMixed: false,
    });

    // Run password validation checks on input change
    useEffect(() => {
        setValidations({
            minLength: newPassword.length >= 8,
            hasNumber: /\d/.test(newPassword),
            hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
            hasMixed: /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword),
        });
    }, [newPassword]);

    const isPasswordValid = Object.values(validations).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            toast.error("Please meet all password requirements first.");
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match!");
            return;
        }

        setLoading(true);
        try {
            // Note: Fixed the string interpolation using backticks and ${}
            await api.post(`password-reset-confirm/${uid}/${token}/`, {
                new_password: newPassword
            });
            
            toast.success("Password reset successfully! Redirecting...");
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            toast.error(err.response?.data?.error || "This link is invalid or has expired.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 font-dynamic-title bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-300">
            <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700/50 p-6 md:p-8 backdrop-blur-sm">
                
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="mx-auto h-12 w-12 rounded-xl bg-[#002855] bg-opacity-80 dark:bg-blue-600 flex items-center justify-center mb-4 shadow-md shadow-blue-900/20">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-sm font-extrabold text-[#002855] dark:text-blue-400 tracking-tight">Secure Your Account</h2>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-2">
                        Please set your new account access password.
                    </p>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit}>
                    
                    {/* New Password Field */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full pl-4 pr-12 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-1 focus:ring-[#002855] dark:focus:ring-blue-500 focus:border-transparent transition duration-200 text-sm placeholder-gray-400 dark:placeholder-gray-500"
                                placeholder="••••••••"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none transition-colors"
                            >
                                {showPassword ? (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Dynamic Password Validation Requirements Panel */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-900/30 border border-gray-100 dark:border-gray-800 rounded-xl space-y-1.5 text-xs">
                        <div className="flex items-center gap-2">
                            <span className={validations.minLength ? "text-green-500" : "text-gray-400"}>●</span>
                            <span className={validations.minLength ? "text-green-600 dark:text-green-400 line-through" : "text-gray-500"}>At least 8 characters</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={validations.hasMixed ? "text-green-500" : "text-gray-400"}>●</span>
                            <span className={validations.hasMixed ? "text-green-600 dark:text-green-400 line-through" : "text-gray-500"}>Uppercase & lowercase letters</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={validations.hasNumber ? "text-green-500" : "text-gray-400"}>●</span>
                            <span className={validations.hasNumber ? "text-green-600 dark:text-green-400 line-through" : "text-gray-500"}>At least one number</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={validations.hasSpecial ? "text-green-500" : "text-gray-400"}>●</span>
                            <span className={validations.hasSpecial ? "text-green-600 dark:text-green-400 line-through" : "text-gray-500"}>At least one special character (!@#...)</span>
                        </div>
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                        <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-2">
                            Confirm New Password
                        </label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#002855] dark:focus:ring-blue-500 focus:border-transparent transition duration-200 text-sm placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="••••••••"
                            required
                        />
                        {confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-red-500 text-xs mt-1 font-medium">Passwords do not match</p>
                        )}
                        {confirmPassword && newPassword === confirmPassword && isPasswordValid && (
                            <p className="text-green-500 text-xs mt-1 font-medium">Passwords match perfectly!</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading || !isPasswordValid || newPassword !== confirmPassword}
                        className={`w-full text-[11px] relative mt-2 py-3 px-4 font-semibold text-white bg-[#002855] bg-opacity-80 hover:bg-opacity-95 dark:bg-blue-600 dark:hover:bg-blue-700 rounded-xl transition duration-200 shadow-md shadow-blue-900/10 active:scale-[0.99] focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-[#002855] dark:focus:ring-blue-500 ${
                            loading || !isPasswordValid || newPassword !== confirmPassword ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center space-x-2">
                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span>Updating Account...</span>
                            </span>
                        ) : (
                            "Reset Password"
                        )}
                    </button>
                </form>

            </div>
        </div>
    );
}